import { useMutation } from '@tanstack/react-query';
import { useApi } from '../../lib/api';

interface ErrorBody {
  error?: string;
  message?: string;
}

export interface DisableMfaError extends Error {
  status?: number;
  body?: ErrorBody;
}

function toError(stage: string, response: Response | undefined, error: unknown): DisableMfaError {
  const e = new Error(stage) as DisableMfaError;
  if (response) e.status = response.status;
  e.body = (error ?? {}) as ErrorBody;
  return e;
}

/**
 * Disables MFA for the current user.
 *
 * The backend `DELETE /auth/mfa` handler is gated by the `RequireStepUp`
 * extractor and additionally validates the password. When the caller's
 * session ACR is insufficient the call fails with 403 `step_up_required`,
 * which the @sentinel/api-client step-up middleware catches and surfaces
 * via the global step-up dialog. The middleware retries the request after
 * step-up succeeds, at which point the password sent in the body is
 * validated against the user's current credential.
 */
export function useDisableMfa() {
  const api = useApi();
  return useMutation<void, DisableMfaError, { password: string }>({
    mutationFn: async ({ password }) => {
      const { error, response } = await api.DELETE('/auth/mfa', { body: { password } });
      if (error) throw toError('mfa_disable_failed', response, error);
    },
  });
}
